/**
 * SSE Adapter for Vercel AI SDK
 * 
 * Converts useChat API calls to SSE requests and parses SSE events
 * into the format expected by Vercel AI SDK.
 */

import { storage } from "./local-storage";

// Build API base URL
const getApiBaseUrl = (): string => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
    const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";
    const cleanBaseUrl = baseUrl.replace(/\/$/, "");
    const cleanVersionPrefix = versionPrefix.startsWith("/") ? versionPrefix : `/${versionPrefix}`;
    return `${cleanBaseUrl}${cleanVersionPrefix}`;
};

const API_BASE_URL = getApiBaseUrl();

function buildApiUrl(endpoint: string): URL {
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL : `${API_BASE_URL}/`;
    const fullUrl = `${baseUrl}${cleanEndpoint}`;
    return new URL(fullUrl);
}

function getAuthToken(): string | null {
    return storage.getItem("access_token");
}

export interface SSEEvent {
    event: string;
    data: any;
    // Additional properties that may be present in parsed SSE data
    delta?: string;
    step_type?: string;
    content?: string;
    agent_name?: string;
    timestamp?: string;
    metadata?: Record<string, any>;
    tool_name?: string;
    input?: any;
    output?: any;
    from_agent?: string;
    to_agent?: string;
    reason?: string;
    message?: string;
    // UI resource and conversation title properties
    resource?: {
        uri: string;
        name?: string;
        mimeType: string;
        text?: string;
        blob?: string;
        _meta?: Record<string, any>;
        [key: string]: any;
    };
    conversation_id?: string;
    title?: string;
}

/**
 * Parse SSE data line into event object
 */
function parseSSELine(line: string): SSEEvent | null {
    if (!line.trim()) return null;
    
    try {
        const parsed = JSON.parse(line);
        return parsed;
    } catch {
        return null;
    }
}

/**
 * Callbacks for processing SSE events
 */
export interface SSEEventCallbacks {
    onThinkingStep?: (step: {
        step_type: string;
        content: string;
        agent_name: string;
        timestamp: string;
        metadata?: Record<string, any>;
    }) => void;
    onToolCall?: (tool: {
        tool_name: string;
        input?: any;
        output?: any;
        agent_name: string;
    }) => void;
    onAgentSwitch?: (delegation: {
        from_agent: string;
        to_agent: string;
        reason: string;
    }) => void;
    onUIResource?: (resource: {
        uri: string;
        name?: string;
        mimeType: string;
        text?: string;
        blob?: string;
        _meta?: Record<string, any>;
        [key: string]: any;
    }) => void;
    onConversationTitle?: (data: { conversation_id: string; title: string }) => void;
}

/**
 * Create SSE adapter function for Vercel AI SDK
 * 
 * This function is used as the `api` option in useChat hook.
 * It converts the request to SSE format and streams the response.
 * 
 * Vercel AI SDK expects either:
 * - A string URL
 * - A function that takes a Request and returns a Response
 */
export function createSSEAdapter(
    conversationId: string,
    callbacks?: SSEEventCallbacks,
    getFiles?: () => File[] | null,
    getDocumentIds?: () => string[] | null,
    getClientContext?: () => Record<string, unknown> | null,
) {
    return async (request: Request): Promise<Response> => {
        
        // Extract body and signal from request
        const body = request.body;
        const signal = request.signal;
        
        // Check if we have files to upload
        const files = getFiles ? getFiles() : null;
        const hasFiles = files && files.length > 0;
        
        // Check if we have document IDs
        const documentIds = getDocumentIds ? getDocumentIds() : null;
        const hasDocumentIds = documentIds && documentIds.length > 0;

        const clientContext = getClientContext ? getClientContext() : null;
        const hasClientContext = clientContext && Object.keys(clientContext).length > 0;
        
        // Read request body
        if (!body) {
            throw new Error("Request body is required");
        }
        
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let requestBody = "";
        
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            requestBody += decoder.decode(value, { stream: true });
        }
        
        if (!requestBody || requestBody.trim() === "") {
            throw new Error("Request body is empty");
        }
        
        let requestData;
        try {
            requestData = JSON.parse(requestBody);
        } catch (e) {
            throw new Error(`Failed to parse request body: ${e instanceof Error ? e.message : String(e)}`);
        }
        
        const message = requestData.messages?.[requestData.messages.length - 1]?.content || "";
        
        // Build SSE endpoint URL
        const url = buildApiUrl(`/conversations/${conversationId}/messages/stream`);
        
        // Get auth token
        const token = getAuthToken();
        
        // Prepare request headers
        const headers: Record<string, string> = {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        
        let requestBodyToSend: string | FormData;
        
        if (hasFiles || hasDocumentIds) {
            // Use FormData for multipart/form-data (when files or document_ids are present)
            const formData = new FormData();
            formData.append("message", message);
            formData.append("enable_rag", String(requestData.enable_rag ?? true));
            if (requestData.knowledge_base_id) {
                formData.append("knowledge_base_id", requestData.knowledge_base_id);
            }
            if (requestData.min_similarity !== undefined) {
                formData.append("min_similarity", String(requestData.min_similarity));
            }
            if (requestData.max_results !== undefined) {
                formData.append("max_results", String(requestData.max_results));
            }
            
            // Append files if any
            if (hasFiles && files) {
                files.forEach((file) => {
                    formData.append("files", file);
                });
            }
            
            // Append document_ids if any (comma-separated string for FormData)
            if (hasDocumentIds && documentIds) {
                formData.append("document_ids", documentIds.join(","));
            }

            if (hasClientContext && clientContext) {
                formData.append("client_context", JSON.stringify(clientContext));
            }
            
            requestBodyToSend = formData;
            // Don't set Content-Type header - browser will set it with boundary
        } else {
            // Use JSON for regular requests
            const sseRequestPayload: Record<string, unknown> = {
                message: message,
                enable_rag: requestData.enable_rag ?? true,
                knowledge_base_id: requestData.knowledge_base_id,
                min_similarity: requestData.min_similarity,
                max_results: requestData.max_results,
            };

            if (hasClientContext && clientContext) {
                sseRequestPayload.client_context = clientContext;
            }
            
            // Add document_ids to JSON payload if present
            if (hasDocumentIds && documentIds) {
                sseRequestPayload.document_ids = documentIds;
            }
            
            headers["Content-Type"] = "application/json";
            requestBodyToSend = JSON.stringify(sseRequestPayload);
        }
        
        // Create SSE request
        const response = await fetch(url.toString(), {
            method: "POST",
            headers,
            body: requestBodyToSend,
            signal,
        });
        
        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Could not read error response');
            throw new Error(`SSE request failed: ${response.status} ${response.statusText}. ${errorText}`);
        }
        
        // Convert SSE stream to ReadableStream for Vercel AI SDK
        // We need to create a custom stream that handles both content and reasoning events
        const stream = new ReadableStream({
            async start(controller) {
                const reader = response.body?.getReader();
                if (!reader) {
                    controller.close();
                    return;
                }
                
                const decoder = new TextDecoder();
                let buffer = "";
                let accumulatedContent = "";
                let currentEventType: string | null = null; // Track current event type
                let isClosed = false; // Track if controller has been closed
                
                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) {
                            break;
                        }
                        
                        buffer += decoder.decode(value, { stream: true });
                        
                        const lines = buffer.split("\n");
                        buffer = lines.pop() || ""; // Keep incomplete line in buffer
                        
                        for (const line of lines) {
                            // Handle SSE event format: "event: <type>" or "data: <json>"
                            if (line.startsWith("event: ")) {
                                // Save event type for next data line
                                currentEventType = line.slice(7).trim(); // Remove "event: " prefix
                                continue;
                            }
                            
                            if (line.startsWith("data: ")) {
                                const data = line.slice(6); // Remove "data: " prefix
                                const parsedData = parseSSELine(data);
                                
                                if (parsedData) {
                                    // Process based on event type
                                    if (currentEventType === "content_delta" || parsedData.delta) {
                                        // Stream content delta as text chunk
                                        const encoder = new TextEncoder();
                                        const delta = parsedData.delta || "";
                                        
                                        if (delta) {
                                            accumulatedContent += delta;
                                            // Vercel AI SDK expects format: "0:\"text chunk\""
                                            // where 0 = text type, : = separator, "text chunk" = JSON-encoded content
                                            // Use JSON.stringify to properly encode the delta string
                                            const formattedChunk = `0:${JSON.stringify(delta)}\n`;
                                            const chunk = encoder.encode(formattedChunk);
                                            controller.enqueue(chunk);
                                        }
                                    } else if (currentEventType === "out_of_scope" && parsedData.content) {
                                        // Handle out_of_scope event that contains the full response content
                                        const encoder = new TextEncoder();
                                        const content = parsedData.content || "";
                                        
                                        if (content) {
                                            // Send the full content as a single chunk
                                            accumulatedContent += content;
                                            const formattedChunk = `0:${JSON.stringify(content)}\n`;
                                            const chunk = encoder.encode(formattedChunk);
                                            controller.enqueue(chunk);
                                        }
                                    } else if (currentEventType === "thinking_step") {
                                        // Process thinking step event
                                        if (callbacks?.onThinkingStep && parsedData.step_type && parsedData.content) {
                                            callbacks.onThinkingStep({
                                                step_type: parsedData.step_type,
                                                content: parsedData.content,
                                                agent_name: parsedData.agent_name || "Unknown",
                                                timestamp: parsedData.timestamp || new Date().toISOString(),
                                                metadata: parsedData.metadata
                                            });
                                        }
                                    } else if (currentEventType === "tool_call") {
                                        // Process tool call event
                                        if (callbacks?.onToolCall && parsedData.tool_name) {
                                            callbacks.onToolCall({
                                                tool_name: parsedData.tool_name,
                                                input: parsedData.input,
                                                output: parsedData.output,
                                                agent_name: parsedData.agent_name || "Unknown"
                                            });
                                        }
                                    } else if (currentEventType === "agent_switch") {
                                        // Process agent switch event
                                        if (callbacks?.onAgentSwitch && parsedData.from_agent && parsedData.to_agent) {
                                            callbacks.onAgentSwitch({
                                                from_agent: parsedData.from_agent,
                                                to_agent: parsedData.to_agent,
                                                reason: parsedData.reason || "Delegated"
                                            });
                                        }
                                    } else if (currentEventType === "ui_resource") {
                                        // Process UI resource event
                                        if (callbacks?.onUIResource && parsedData.resource) {
                                            callbacks.onUIResource(parsedData.resource);
                                        }
                                    } else if (currentEventType === "conversation_title") {
                                        if (callbacks?.onConversationTitle && parsedData.conversation_id && parsedData.title) {
                                            callbacks.onConversationTitle({
                                                conversation_id: parsedData.conversation_id,
                                                title: parsedData.title,
                                            });
                                        }
                                    } else if (currentEventType === "done") {
                                        // Signal completion
                                        if (!isClosed) {
                                            controller.close();
                                            isClosed = true;
                                        }
                                        return;
                                    } else if (currentEventType === "error" || parsedData.message) {
                                        // Signal error
                                        if (!isClosed) {
                                            controller.error(new Error(parsedData.message || "Stream error"));
                                            isClosed = true;
                                        }
                                        return;
                                    }
                                }
                                
                                // Reset event type after processing
                                currentEventType = null;
                            }
                        }
                    }
                } catch (error) {
                    if (!isClosed) {
                        controller.error(error);
                        isClosed = true;
                    }
                } finally {
                    // Only close if not already closed
                    if (!isClosed) {
                        controller.close();
                        isClosed = true;
                    }
                    // Release reader lock
                    try {
                        reader.releaseLock();
                    } catch (e) {
                        // Reader might already be released, ignore
                    }
                }
            },
        });
        
        return new Response(stream, {
            headers: {
                "Content-Type": "text/plain; charset=utf-8",
            },
        });
    };
}

/**
 * Parse SSE stream for reasoning events
 * 
 * This function can be used to extract thinking steps, tool calls, etc.
 * from the SSE stream separately from the content stream.
 */
export async function* parseSSEStream(
    response: Response
): AsyncGenerator<SSEEvent, void, unknown> {
    if (!response.body) return;
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            
            for (const line of lines) {
                if (line.startsWith("data: ")) {
                    const data = line.slice(6);
                    const event = parseSSELine(data);
                    if (event) {
                        yield event;
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}

